/**
 * AuthGateScene — the proxy PASSWORD GATE (CLAUDE.md: "the client shows a password
 * screen once, exchanges it for a token, and sends it as a bearer on /api/*").
 *
 * THIS WAS THE MISSING PIECE. Nothing in the client ever called POST /api/auth or
 * stored the bearer token, so `localStorage['pr:proxyToken']` was always empty →
 * serverSave.canSync() was always false → every durable PUT was silently skipped and
 * the server-side save store stayed empty (phone + laptop only ever saw their own
 * IndexedDB). The same null also disabled LLM chat. This gate fills that gap.
 *
 * Flow: Boot → Preload → *AuthGate* → Title → (Town | NewGame).
 *
 * Decompression-first / never-blocks-play (the triple contract): the gate AUTO-SKIPS
 * straight to Title when it isn't needed — a token is already stored, the proxy is
 * unreachable (offline / no server), or the server reports no password is configured
 * (dev). When a password IS required, the player may still tap "Play offline" to enter
 * the world on the template floor and authenticate later. A wrong password shows a
 * gentle retry, never a wall.
 *
 * Touch-first + responsive: the password TextInput pops the OS keyboard on phones; the
 * buttons are big tap targets placed via Layout, relaying out on resize.
 */
import Phaser from 'phaser';
import { authenticate, probeAuthPosture } from '@/store';
import { COLORS, CSS, FONT, SPACING } from './ui/theme.js';
import { Button } from './ui/Button.js';
import { TextInput } from './ui/TextInput.js';
import { Layout } from './layout/index.js';
import { fadeIn, goTo, isReducedMotion } from './flow.js';
import { authDeps } from './session.js';

export class AuthGateScene extends Phaser.Scene {
  private layout!: Layout;
  private title?: Phaser.GameObjects.Text;
  private hint?: Phaser.GameObjects.Text;
  private status?: Phaser.GameObjects.Text;
  private passwordInput?: TextInput;
  private enterButton?: Button;
  private skipButton?: Button;
  private reduced = false;
  /** Set while a probe / login is in flight so we never double-fire. */
  private busy = false;
  /** True once we've decided the gate IS needed and built the form. */
  private formShown = false;
  /** Single-fire guard for the navigation to Title (submit + skip both reach it). */
  private navigated = false;

  constructor() {
    super('AuthGate');
  }

  create(): void {
    // Phaser reuses the scene instance — reset transient state so a re-entry never
    // starts latched-busy or with a stale form flag.
    this.busy = false;
    this.formShown = false;
    this.navigated = false;
    this.title = undefined;
    this.hint = undefined;
    this.status = undefined;
    this.passwordInput = undefined;
    this.enterButton = undefined;
    this.skipButton = undefined;

    this.cameras.main.setBackgroundColor(COLORS.bg);
    this.layout = Layout.attach(this);
    this.reduced = isReducedMotion();

    this.title = this.add
      .text(0, 0, 'Parallel Routes', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.title}px`,
        color: CSS.text,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.status = this.add
      .text(0, 0, 'reaching the town…', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.sm}px`,
        color: CSS.textMuted,
      })
      .setOrigin(0.5);

    this.relayout();
    this.layout.on('change', () => this.relayout());
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    fadeIn(this);

    void this.decide();
  }

  /**
   * Probe the proxy to decide whether to show the password form or skip straight to
   * Title. Best-effort: any failure (offline, no proxy) skips — the game must never be
   * blocked by an unreachable proxy.
   */
  private async decide(): Promise<void> {
    const posture = await probeAuthPosture(authDeps()).catch(() => null);
    if (!this.scene.isActive()) return;

    // Token already stored, proxy unreachable, or no password configured → no gate.
    if (!posture || !posture.needsPassword) {
      this.toTitle();
      return;
    }

    this.showForm();
  }

  /** Build the password form (only when the gate is actually needed). */
  private showForm(): void {
    if (this.formShown) return;
    this.formShown = true;

    this.status?.setText('this region is password-protected');

    this.hint = this.add
      .text(0, 0, 'enter the password to wake the town (chat + cloud saves)', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.xs}px`,
        color: CSS.textFaint,
        align: 'center',
        wordWrap: { width: Math.min(360, this.layout.safe.width - SPACING.lg * 2) },
      })
      .setOrigin(0.5);

    this.passwordInput = new TextInput(this, 0, 0, {
      width: Math.min(300, this.layout.safe.width - SPACING.lg * 2),
      placeholder: 'password',
      maxLength: 128,
      password: true,
      onSubmit: () => void this.submit(),
    });

    this.enterButton = new Button(this, 0, 0, {
      label: 'Enter',
      variant: 'primary',
      width: 220,
      reducedMotion: this.reduced,
      onClick: () => void this.submit(),
    });

    // Never block play: the player can step in on the template floor and log in later.
    this.skipButton = new Button(this, 0, 0, {
      label: 'Play offline',
      variant: 'ghost',
      width: 220,
      reducedMotion: this.reduced,
      onClick: () => this.toTitle(),
    });

    this.relayout();
  }

  /** Exchange the typed password for a token, store it, then continue to Title. */
  private async submit(): Promise<void> {
    if (this.busy) return;
    const password = this.passwordInput?.getValue().trim() ?? '';
    if (password.length === 0) {
      this.status?.setText('please type the password');
      return;
    }

    this.busy = true;
    this.enterButton?.setDisabled(true);
    this.skipButton?.setDisabled(true);
    this.passwordInput?.blur();
    this.status?.setText('waking the town…');

    // authenticate() is non-throwing and stores the token on success.
    const token = await authenticate(password, authDeps());
    if (!this.scene.isActive()) return;

    if (token) {
      this.toTitle();
      return;
    }

    // Wrong password / offline — gentle retry, never a wall.
    this.busy = false;
    this.enterButton?.setDisabled(false);
    this.skipButton?.setDisabled(false);
    this.status?.setText('that password did not match — try again, or play offline');
  }

  /** Step on to the Title (idempotent — submit() and skip() can both reach here). */
  private toTitle(): void {
    if (this.navigated) return;
    this.navigated = true;
    goTo(this, 'Title', { data: { fromAuthGate: true } });
  }

  private relayout(): void {
    const cx = this.layout.centerX;
    const cy = this.layout.centerY;

    this.title?.setPosition(cx, cy - SPACING.xxl * 2);

    if (!this.formShown) {
      this.status?.setPosition(cx, cy);
      return;
    }

    // Form layout: status · hint · input · Enter · Play offline, stacked + centered.
    let y = cy - SPACING.xxl;
    this.status?.setPosition(cx, y);
    y += SPACING.xl;
    this.hint?.setPosition(cx, y);
    y += SPACING.xl;
    this.passwordInput?.setPosition(cx, y);
    y += SPACING.xxl;
    this.enterButton?.setPosition(cx, y);
    y += (this.enterButton?.buttonHeight ?? 48) + SPACING.md;
    this.skipButton?.setPosition(cx, y);
  }

  private teardown(): void {
    this.tweens.killAll();
    this.passwordInput?.blur();
    this.title?.destroy();
    this.hint?.destroy();
    this.status?.destroy();
    this.passwordInput?.destroy();
    this.enterButton?.destroy();
    this.skipButton?.destroy();
    this.title = undefined;
    this.hint = undefined;
    this.status = undefined;
    this.passwordInput = undefined;
    this.enterButton = undefined;
    this.skipButton = undefined;
  }
}
